import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard,
  MessageSquare, 
  Users, 
  Settings, 
  Zap, 
  BarChart2,
  ChevronLeft,
  AppWindow,
  UserCircle,
  BookOpen,
  Home
} from 'lucide-react';
import { useSidebarState } from '../../hooks/useSidebarState';

// Regroupement des éléments de menu par catégorie pour une meilleure organisation
const menuGroups = [
  {
    title: 'Main',
    items: [
      { icon: LayoutDashboard, label: 'Overview', path: '/app/dashboard' },
      { icon: MessageSquare, label: 'Conversations', path: '/app/conversations' },
      { icon: Users, label: 'Collected leads', path: '/app/visitors' },
      { icon: BarChart2, label: 'Statistics', path: '/app/statistics' },
    ]
  },
  {
    title: 'Configuration',
    items: [
      { icon: Zap, label: 'Integrations', path: '/app/integrations' },
      { icon: Settings, label: 'Chatbot Settings', path: '/app/settings/design' },
      { icon: AppWindow, label: 'Mini App Settings', path: '/app/settings/mini-apps' },
    ]
  },
  {
    title: 'Account',
    items: [
      { icon: BookOpen, label: 'Operating instructions', path: '/app/settings/general' },
      { icon: UserCircle, label: 'Account', path: '/app/settings/profiles' },
    ]
  }
];

export default function DashboardSidebar() {
  const { isExpanded, toggle } = useSidebarState();
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [previousPath, setPreviousPath] = useState<string | null>(null);

  // Effet pour suivre les changements de page pour les animations
  useEffect(() => {
    setPreviousPath(location.pathname);
  }, [location.pathname]);

  // Déterminer si nous venons de naviguer vers cette page
  const isNewNavigation = (path: string) => {
    return path === location.pathname && path !== previousPath;
  };

  return (
    <div 
      className={`fixed left-0 top-16 h-[calc(100vh-4rem)] transition-all duration-500 ease-in-out flex flex-col z-10 ${
        isExpanded ? 'w-64' : 'w-20'
      }`}
    >
      {/* Fond avec effet de glassmorphism et dégradé subtil */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-xl"></div>
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black opacity-80"></div>
      
      {/* Effet de lueur subtil sur les bords */}
      <div className="absolute right-0 top-0 bottom-0 w-px bg-gradient-to-b from-blue-500/10 via-violet-500/10 to-transparent"></div>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/10 to-transparent"></div>
      
      {/* Particules/dégradés d'ambiance */}
      <div className="absolute -top-20 -left-20 w-60 h-60 rounded-full bg-gradient-to-br from-blue-500/5 to-indigo-500/5 blur-3xl"></div>
      <div className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full bg-gradient-to-br from-violet-500/5 to-purple-500/5 blur-3xl"></div>

      {/* Bouton de toggle avec animation améliorée */}
      <button
        onClick={toggle}
        className="absolute -right-3 top-6 w-8 h-8 flex items-center justify-center bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full shadow-lg shadow-indigo-950/40 hover:shadow-indigo-500/30 hover:scale-105 transition-all duration-300 z-20 group"
        aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
      >
        <ChevronLeft 
          className={`w-5 h-5 text-white transition-all duration-500 ${
            !isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Menu scrollable - contenu de la sidebar */}
      <div className="relative flex-1 overflow-y-auto py-8 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent hover:scrollbar-thumb-white/20 z-10">
        <div className="px-4 space-y-8">
          {menuGroups.map((group, groupIndex) => (
            <div key={group.title} className="space-y-2">
              {/* Titre du groupe - uniquement visible lorsque la sidebar est développée */}
              {isExpanded && (
                <h3 className={`text-xs font-medium text-gray-400 uppercase tracking-wider px-3 transition-opacity duration-300 ${
                  isExpanded ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden'
                }`}>
                  {group.title}
                </h3>
              )}

              {/* Éléments du groupe */}
              <div className="space-y-1">
                {group.items.map((item) => {
                  const isActive = location.pathname.startsWith(item.path);
                  const isHovered = hoveredItem === item.path;
                  const isNewlyActive = isNewNavigation(item.path);
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onMouseEnter={() => setHoveredItem(item.path)}
                      onMouseLeave={() => setHoveredItem(null)}
                      className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-300 overflow-hidden group ${
                        isExpanded ? 'mx-2' : 'mx-auto w-12 justify-center'
                      } ${
                        isActive 
                          ? 'text-white' 
                          : 'text-gray-400 hover:text-white'
                      }`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {/* Effet de fond animé lors du hover ou état actif */}
                      {(isActive || isHovered) && (
                        <span 
                          className={`absolute inset-0 rounded-xl transition-all duration-300 ${
                            isActive 
                              ? 'bg-gradient-to-r from-blue-600/20 to-indigo-600/20 border border-blue-500/30'
                              : 'bg-white/5'
                          }`}
                          style={{
                            animationName: isNewlyActive ? 'sidebar-indicator-appear' : 'none',
                            animationDuration: '0.5s',
                            animationTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
                            animationFillMode: 'backwards'
                          }}
                        ></span>
                      )}

                      {/* Indicateur de menu actif */}
                      {isActive && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-gradient-to-b from-blue-400 to-indigo-500 rounded-r-full shadow-[0_0_8px_rgba(59,130,246,0.5)]"></span>
                      )}

                      {/* Icône avec animation */}
                      <item.icon className={`w-5 h-5 shrink-0 transition-all duration-300 ${
                        isActive ? 'text-blue-400' : 'text-gray-400 group-hover:text-white'
                      } ${
                        isHovered && !isActive ? 'scale-110 rotate-3' : ''
                      }`} />

                      {/* Texte avec animation */}
                      {isExpanded && (
                        <span 
                          className={`truncate transition-all duration-300 ${
                            isActive ? 'font-medium' : ''
                          }`}
                          style={{
                            animationName: isNewlyActive ? 'sidebar-text-appear' : 'none',
                            animationDuration: '0.3s',
                            animationFillMode: 'backwards'
                          }}
                        >
                          {item.label}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Logo ou élément de pied de page */}
      <div className="relative shrink-0 p-4 border-t border-white/10 z-10 flex justify-center bg-black/20 backdrop-blur-sm">
        <Link 
          to="/"
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <Home className="w-5 h-5" />
          {isExpanded && <span className="text-sm">Home</span>}
        </Link>
      </div>
    </div>
  );
}